import { FunctionComponent, useMemo, useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Calendar from "./Calendar";
import { Controller, useForm } from "react-hook-form";
import DateSelector from "../../components/common/DateSelector";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_CALENDAR_MEETINGS,
  MEETING_TYPES,
  EMPLOYEE_TASKS_LIST,
  PROJECT_GOALS_SHORT,
  PODS_DATA,
} from "../../api/query";
import { addDays, endOfMonth, format, startOfMonth } from "date-fns";
import Loader from "../../components/common/Loader";
import backArrow from "../../assets/task/back_arrow.svg";
import { useNavigate } from "react-router-dom";
import InputsDropdown from "../../components/common/InputsDropdown";
import MemberListPopup from "./MemberListPopup";

const statusOptions = [
  { key: "All", name: "All" },
  { key: "Not Started", name: "Not Started" },
  { key: "In Progress", name: "In Progress" },
  { key: "Testing", name: "Testing" },
  { key: "Closed", name: "Closed" },
];

const ViewCalendar: FunctionComponent = () => {
  const {
    userInfo,
    startDateCalendarDay,
    setStartDateCalendar,
    setStartDateCalendarDay,
    setEndDateCalendar,
    startDateCalendar,
    setCalendarDate,
    setCurrentView,
    calendarDate,
    currentView,
    endDateCalendar,
    calendarFilter,
    setCalendarFilter,
    taskFilter,
    setTaskFilter,
    goalFilter,
    setGoalFilter,
  } = useAuth();

  const employeeID = userInfo?.employee_id || "";
  const agencyId = userInfo?.agencies?.id;
  const navigate = useNavigate();

  // "0" = All Pods
  const [selectedPod, setSelectedPod] = useState("0");
  const [selectedPodName, setSelectedPodName] = useState("All Pods");

  const [memberPopup, setMemberPopup] = useState<{
    open: boolean;
    title: string;
    members: any[];
    columns: {
      key: string;
      label: string;
    }[];
    onViewItem?: (item: any) => void;
  }>({
    open: false,
    title: "",
    members: [],
    columns: [],
  });

  const { control } = useForm<any>();

  // ==================== MEETINGS QUERIES ====================
  const { data: meetingTypesData, loading: meetingTypesDataLoading } = useQuery(
    MEETING_TYPES,
    { fetchPolicy: "no-cache" },
  );

  const [getMeetings, { data: meetingsResponse, loading: meetingsLoading }] =
    useLazyQuery(GET_CALENDAR_MEETINGS, { fetchPolicy: "no-cache" });

  // ==================== TASKS QUERIES ====================
  const [
    getEmployeeTaskList,
    { data: employeeTaskListData, loading: tasksLoading },
  ] = useLazyQuery(EMPLOYEE_TASKS_LIST, { fetchPolicy: "no-cache" });

  // ==================== GOALS QUERIES  ====================
  const [getEmployeeGoalList, { data: goalsData, loading: goalsLoading }] =
    useLazyQuery(PROJECT_GOALS_SHORT, { fetchPolicy: "no-cache" });

  // ==================== POD QUERIES  ====================
  const [getPOD, { data: podData, loading: podLoading }] = useLazyQuery(
    PODS_DATA,
    { fetchPolicy: "no-cache" },
  );

  // ==================== POD DATA ====================
  const podDataFormatted = useMemo(
    () => podData?.pods_data?.data || [],
    [podData],
  );

  // Only the pods in which the logged-in user is a member
  const myPods = useMemo(() => {
    return podDataFormatted.filter((pod: any) =>
      (pod.members || []).some(
        (m: any) => String(m.employee_id) === String(employeeID),
      ),
    );
  }, [podDataFormatted, employeeID]);

  const podOptions = useMemo(
    () => [
      ...myPods.map((pod: any) => ({
        key: String(pod.pod_id),
        name: pod.pod_name,
      })),
    ],
    [myPods],
  );

  const podIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    podDataFormatted.forEach((pod: any) => {
      map.set(String(pod.pod_id), pod.pod_name);
    });
    return map;
  }, [podDataFormatted]);

  // ==================== MEETING TYPE OPTIONS ====================
  const meetingTypeOptions = useMemo(() => {
    const meetingTypes = meetingTypesData?.meeting_types?.meeting_types || [];
    return [
      { key: "0", name: "All" },
      ...meetingTypes.map((mt: any) => ({
        key: mt.id,
        name: mt.name,
      })),
    ];
  }, [meetingTypesData]);

  const selectedMeetingName = useMemo(() => {
    if (calendarFilter === 0) return "All";
    const selected = meetingTypeOptions.find(
      (option) => option.key === String(calendarFilter),
    );
    return selected?.name || "All";
  }, [calendarFilter, meetingTypeOptions]);

  // ==================== MEETINGS DATA ====================
  const meetingData = useMemo(() => {
    return (
      meetingsResponse?.get_meetings?.meetings?.filter(
        (meeting: any) => meeting.is_deleted === false,
      ) || []
    );
  }, [meetingsResponse]);

  // ==================== TASKS DATA ====================
  const tasksData = useMemo(() => {
    return (
      employeeTaskListData?.employee_tasks_list?.tasks?.filter(
        (task: any) => task.start_datetime && task.end_datetime,
      )?.map((t: any) => {
        return {
          ...t,
          current_status_name: t.static_task_type_name === "Production Release" ? t.static_task_type_name : t.current_status_name,
          current_status: t.current_status_name
        }
      }) || []
    );
  }, [employeeTaskListData]);

  // ==================== GOALS DATA ====================
  const goalsDataFormatted = useMemo(() => {
    const groupedGoals = goalsData?.project_goals_short?.grouped_goals || [];
    const formattedGoals: any[] = [];

    groupedGoals.forEach((group: any) => {
      group.goals?.forEach((goal: any) => {
        // Convert goal dates to datetime format if needed
        const startDateTime = goal.start_date
          ? new Date(goal.start_date).toISOString()
          : null;
        const endDateTime = goal.end_date
          ? new Date(goal.end_date).toISOString()
          : null;

        // Only include goals with date ranges
        if (startDateTime && endDateTime) {
          formattedGoals.push({
            id: goal.id,
            title: goal.title,
            start_datetime: startDateTime,
            end_datetime: endDateTime,
            created_by: goal.created_by,
            actual_start_date: goal.actual_start_date,
            actual_end_date: goal.actual_end_date,
            goal_static_outcome: goal.goal_static_outcome,
            goal_members: goal.goal_members,
            status_name: group.status_name, // Include the status from grouped_goals
            status_id: group.status_id,
            project_name: goal.project_name,
            pod_id: goal.pod_id, // used for pod filtering
            event_type: "goal", // Add event type identifier
          });
        }
      });
    });

    return formattedGoals;
  }, [goalsData]);

  // ==================== POD FILTERING ====================
  // All data is already fetched for the logged-in employee only, so filtering
  // by pod_id only narrows the user's own meetings / tasks / goals.
  const matchesPod = useCallback(
    (item: any) =>
      selectedPod === "0" || String(item.pod_id) === String(selectedPod),
    [selectedPod],
  );

  const filteredMeetings = useMemo(
    () => meetingData.filter(matchesPod),
    [meetingData, matchesPod],
  );

  const filteredTasks = useMemo(
    () => tasksData.filter(matchesPod),
    [tasksData, matchesPod],
  );

  const filteredGoals = useMemo(
    () => goalsDataFormatted.filter(matchesPod),
    [goalsDataFormatted, matchesPod],
  );

  // ==================== MEETINGS FOR POPUP ====================
  const meetingsForPopup = useMemo(() => {
    return filteredMeetings.map((meeting: any) => ({
      title: meeting.title,
      dateTime: `${format(new Date(meeting.start_datetime), "MM/dd/yyyy hh:mm a")} - ${format(new Date(meeting.end_datetime), "hh:mm a")}`,
      meeting_type_name: meeting.meeting_type_name || "-",
      _meetingId: meeting.id,
      _startMeetDate: format(new Date(meeting.start_datetime), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
      _endMeetDate: format(new Date(meeting.end_datetime), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    }));
  }, [filteredMeetings]);

  // ==================== COMBINED EVENTS ====================
  // Combine meetings, tasks, and goals into a single array for the calendar
  const combinedEvents = useMemo(() => {
    return [...filteredMeetings, ...filteredTasks, ...filteredGoals];
  }, [filteredMeetings, filteredTasks, filteredGoals]);

  // ==================== FETCH LOGIC ====================
  const fetchMeetings = useCallback(() => {
    if (
      !agencyId ||
      !employeeID ||
      calendarFilter === undefined ||
      calendarFilter === null
    ) {
      return;
    }

    getMeetings({
      variables: {
        start_date: format(startDateCalendar, "yyyy-MM-dd'T'00:00:00'Z'"),
        end_date: format(endDateCalendar, "yyyy-MM-dd'T'23:59:59'Z'"),
        agency_id: Number(agencyId),
        employee_id: parseInt(employeeID),
        meeting_type_ids: calendarFilter === 0 ? null : [calendarFilter],
      },
    });
  }, [
    agencyId,
    employeeID,
    calendarFilter,
    getMeetings,
    startDateCalendar,
    endDateCalendar,
  ]);

  const fetchTasks = useCallback(() => {
    if (!employeeID) {
      return;
    }

    getEmployeeTaskList({
      variables: {
        employee_id: parseInt(employeeID),
        start_date: format(startDateCalendar, "yyyy-MM-dd'T'00:00:00'Z'"),
        end_date: format(endDateCalendar, "yyyy-MM-dd'T'23:59:59'Z'"),
        page: 1,
        page_size: 400,
        task_status: taskFilter === "All" ? null : taskFilter,
      },
    });
  }, [
    employeeID,
    getEmployeeTaskList,
    startDateCalendar,
    endDateCalendar,
    taskFilter,
  ]);

  const fetchGoals = useCallback(() => {
    if (!employeeID) {
      return;
    }
    getEmployeeGoalList({
      variables: {
        employee_id: parseInt(employeeID),
        start_date: format(startDateCalendar, "yyyy-MM-dd"),
        end_date: format(endDateCalendar, "yyyy-MM-dd"),
        is_archived: false,
        goal_status: goalFilter === "All" ? null : goalFilter,
      },
    });
  }, [
    employeeID,
    getEmployeeGoalList,
    startDateCalendar,
    endDateCalendar,
    goalFilter,
  ]);

  const fetchPod = useCallback(() => {
    if (!employeeID || !agencyId) {
      return;
    }

    getPOD({
      variables: {
        start_date: format(startDateCalendar, "yyyy-MM-dd"),
        end_date: format(endDateCalendar, "yyyy-MM-dd"),
        agency_id: parseInt(agencyId),
      },
    });
  }, [employeeID, agencyId, getPOD, startDateCalendar, endDateCalendar]);

  // Fetch meetings, goals , tasks, pods when dependencies change
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  useEffect(() => {
    fetchPod();
  }, [fetchPod]);

  // ==================== DATE RANGE EFFECT ====================
  useEffect(() => {
    if (currentView === "timelineDay") {
      setStartDateCalendar(startDateCalendarDay);
      setEndDateCalendar(startDateCalendarDay);
    } else {
      setStartDateCalendar(calendarDate.start);
      setEndDateCalendar(calendarDate.end);
    }
  }, [
    currentView,
    startDateCalendarDay,
    calendarDate,
    setStartDateCalendar,
    setEndDateCalendar,
  ]);

  // ==================== EVENT HANDLERS ====================
  const handleStartDateChange = useCallback(
    (date: string | null | any) => {
      if (currentView === "timelineWeek") {
        const dateAfter7Days = addDays(date, 7);
        const formattedDateEnd = format(dateAfter7Days, "yyyy-MM-dd");
        setStartDateCalendar(date);
        setEndDateCalendar(formattedDateEnd);
      } else if (currentView === "dayGridMonth") {
        const firstDayOfMonth = startOfMonth(new Date(date));
        const formattedStartDate = format(firstDayOfMonth, "yyyy-MM-dd");
        const lastDayOfMonth = endOfMonth(new Date(date));
        const formattedEndDate = format(lastDayOfMonth, "yyyy-MM-dd");

        setStartDateCalendar(formattedStartDate);
        setEndDateCalendar(formattedEndDate);
      } else {
        setEndDateCalendar(date);
        setStartDateCalendar(date);
        setStartDateCalendarDay(date);
      }
    },
    [
      currentView,
      setStartDateCalendar,
      setEndDateCalendar,
      setStartDateCalendarDay,
    ],
  );

  const handleFilterClick = useCallback(
    (key: any, name: any) => {
      setCalendarFilter(parseInt(key));
    },
    [setCalendarFilter],
  );

  const handleTaskFilterClick = useCallback((key: string) => {
    setTaskFilter(key);
  }, []);

  const handleGoalFilterClick = useCallback((key: string) => {
    setGoalFilter(key);
  }, []);

  const handlePodSelect = useCallback((key: any, name: any) => {
    setSelectedPod(String(key));
    setSelectedPodName(name);
  }, []);

  const totalMeetings = filteredMeetings.length;
  const totalTasks = filteredTasks.length;
  const totalGoals = filteredGoals.length;

  // ==================== RELEASE TASKS ====================
  const releaseTasksData = useMemo(() => {
    return filteredTasks.filter(
      (task: any) => task.static_task_type_name === "Production Release",
    );
  }, [filteredTasks]);

  const releaseTasksForPopup = useMemo(() => {
    return releaseTasksData.map((task: any) => {
      return {
        taskName: task.title,
        goal_name: task.goal_name || "-",
        project_name: task.project_name || "-",
        podName: podIdToNameMap.get(String(task.pod_id)) || "-",
        employeeName: task.assigned_to_name || "-",
        date: task.start_datetime
          ? format(new Date(task.start_datetime), "MM/dd/yyyy")
          : "-",
        status: task?.current_status
      };
    });
  }, [releaseTasksData, podIdToNameMap]);

  const isLoading =
    meetingsLoading ||
    tasksLoading ||
    meetingTypesDataLoading ||
    goalsLoading ||
    podLoading;

  return (
    <div
      className="w-full relative min-h-screen overflow-hidden shrink-0 flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope gap-[24px]"
      id="cts-respon-p-dv"
    >
      <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl w-full">
        <img
          className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
          alt="back"
          src={backArrow}
          onClick={() => navigate(-1)}
        />
        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
          My Calendar
        </h3>
      </div>

      <div className="m-0 self-stretch flex flex-col items-start justify-start box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border ctc-calender-main-dv main_calender_main_blg">
        {/* Filters Row */}
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 items-end gap-[16px] min-w-full">
          <Controller
            name="start_date"
            control={control}
            render={({ field }) => (
              <DateSelector
                label="Start Date*"
                selectedDate={startDateCalendar}
                onChange={(date) => {
                  field.onChange(date);
                  handleStartDateChange(date);
                }}
                nothideParsedValue={true}
                blueIcon={true}
              />
            )}
          />
          <div className="min-w-full">
            <InputsDropdown
              label="Meeting Filter"
              placeHolder="All Meetings"
              options={meetingTypeOptions}
              onSelect={handleFilterClick}
              initialValue={selectedMeetingName}
            />
          </div>
          <div className="min-w-full">
            <InputsDropdown
              label="Task Filter"
              placeHolder="All Tasks"
              options={statusOptions}
              onSelect={handleTaskFilterClick}
              initialValue={taskFilter}
            />
          </div>

          <div className="min-w-full">
            <InputsDropdown
              label="Goal Filter"
              placeHolder="All Goals"
              options={statusOptions}
              onSelect={handleGoalFilterClick}
              initialValue={goalFilter}
            />
          </div>
        </div>

        {/* Pod Filter Row */}
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 items-end gap-[16px] min-w-full">
          <div className="min-w-full">
            <InputsDropdown
              label="Capability Pod"
              placeHolder="All Pods"
              options={podOptions}
              onSelect={handlePodSelect}
              initialValue={selectedPodName}
            />
          </div>
        </div>

        {/* Legend / Info */}
        <div className="grid flex-row gap-4 mq991:gap-4  text-xs mq575:flex-col bg-white p-5 self-stretch ">
          <div className="flex gap-8 w-full">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() =>
                setMemberPopup({
                  open: true,
                  title: "Meetings",
                  members: meetingsForPopup,
                  columns: [
                    { key: "title", label: "Title" },
                    { key: "dateTime", label: "Date/Time" },
                    { key: "meeting_type_name", label: "Type" },
                    { key: "_view", label: "View" },
                  ],
                  onViewItem: (item) =>
                    navigate("/meeting_detail", {
                      state: {
                        meetingID: item._meetingId,
                        startMeetDate: item._startMeetDate,
                        endMeetDate: item._endMeetDate,
                      },
                    }),
                })
              }
            >
              <div className="w-4 h-4 rounded bg-blue-500" />
              <span className="text-gray-600 dark:text-gray-400">Meetings</span>
              <span className="emplink-item-dv text-sm font-semibold dark:text-white">
                ({filteredMeetings.length})
              </span>
            </div>
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() =>
                setMemberPopup({
                  open: true,
                  title: "Production Release Tasks",
                  members: releaseTasksForPopup,
                  columns: [
                    { key: "taskName", label: "Task" },
                    { key: "goal_name", label: "Goal" },
                    { key: "project_name", label: "Project" },
                    { key: "podName", label: "Pod" },
                    { key: "employeeName", label: "Employee" },
                    { key: "date", label: "Date" },
                    { key: "status", label: "Status" },
                  ],
                })
              }
            >
              <div className="w-4 h-4 rounded bg-release-item" />
              <span className="text-gray-600 dark:text-gray-400">Release</span>
              <span className="emplink-item-dv text-sm font-semibold dark:text-white">
                ({releaseTasksData.length})
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-aliceblue" />
              <span className="text-gray-600 dark:text-gray-400">
                Not Started
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-antiquewhite" />
              <span className="text-gray-600 dark:text-gray-400">
                In Progress
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-antiquewhite-testing " />
              <span className="text-gray-600 dark:text-gray-400">Testing</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-honeydew" />
              <span className="text-gray-600 dark:text-gray-400">Closed</span>
            </div>

          </div>
          <div className="flex gap-8 w-full">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Total Meetings:</span>{" "}
              {totalMeetings}
            </div>

            <div className="flex items-center gap-2">
              <span className="font-semibold">Total Tasks:</span> {totalTasks}
            </div>

            <div className="flex items-center gap-2">
              <span className="font-semibold">Total Goals:</span> {totalGoals}
            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className="self-stretch flex flex-col items-start justify-start [row-gap:20px] mq950:flex-wrap wizkind_calendar_main_dv calendar-leftbar-dv">
          {startDateCalendar && (
            <div className="shadow-[2px_2px_32px_rgba(0,_19,_85,_0.08)] rounded-lg bg-white box-border overflow-hidden shrink-0 flex items-end justify-start min-w-full mq825:min-w-full max-w-full text-xs text-text-dark-shade border-[1px] border-stroke mq1425:flex-1 caldender-right-main-blg">
              <Calendar
                setCalendarDate={setCalendarDate}
                setCurrentView={setCurrentView}
                startDate={startDateCalendar}
                calendarDate={calendarDate}
                setStartDateCalendar={setStartDateCalendar}
                setEndDateCalendar={setEndDateCalendar}
                setStartDateCalendarDay={setStartDateCalendarDay}
                currentView={currentView}
                filteredData={combinedEvents}
              />
            </div>
          )}
        </div>
      </div>

      {isLoading && <Loader />}
      <div className="pdo-modelpup-ui-item">
        <MemberListPopup
          open={memberPopup.open}
          title={memberPopup.title}
          members={memberPopup.members}
          columns={memberPopup.columns}
          onViewItem={memberPopup.onViewItem}
          onClose={() =>
            setMemberPopup({
              open: false,
              title: "",
              members: [],
              columns: [],
            })
          }
        />
      </div>
    </div>
  );
};

export default ViewCalendar;